import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/utils/api';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('system');
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load theme from user settings on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        console.log('Loading theme settings...');
        const settings = await api.getSettings();
        console.log('Loaded settings:', settings);
        const savedTheme = settings.theme as Theme;
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          setThemeState(savedTheme);
        }
      } catch (err) {
        console.error('Failed to load theme settings:', err);
        // Fallback to system preference
        setThemeState('system');
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    loadTheme();
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (!isInitialized) return;

    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    let appliedTheme: 'light' | 'dark';
    if (theme === 'system') {
      appliedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    } else {
      appliedTheme = theme;
    }
    
    root.classList.add(appliedTheme);
    console.log('Theme applied:', appliedTheme, '(selected:', theme, ')');
  }, [theme, isInitialized]);

  // Listen for system theme changes
  useEffect(() => {
    if (!isInitialized || theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(mediaQuery.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, isInitialized]);

  const setTheme = async (newTheme: Theme) => {
    // Apply immediately (optimistic UI)
    setThemeState(newTheme);
    
    try {
      await api.updateSettings({ theme: newTheme });
      console.log('Theme saved:', newTheme);
    } catch (err) {
      console.error('Failed to save theme:', err);
      // Show error but don't revert - user experience is better this way
      // The theme will reset on next page load if it wasn't saved
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
